import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Typography } from '@kustom-checkout/morpheus';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { getConfig } from '~/api';
import type { AppConfig } from '~/api/types';

interface ConfigProviderProps {
  children: (config: AppConfig) => ReactNode;
}

function CenteredMessage({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Typography variant="h1">{title}</Typography>
      <div className="flex flex-col items-center gap-4">{children}</div>
    </div>
  );
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setHasError(false);
      setIsLoading(true);
      const { data } = await getConfig();
      setConfig(data);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  if (isLoading) {
    return (
      <CenteredMessage title="Loading config">
        <Typography variant="p" color="muted">
          Please wait while config is loading.
        </Typography>
        <Loader2 className="text-on-surface-muted size-8 animate-spin" />
      </CenteredMessage>
    );
  }

  if (hasError || !config) {
    return (
      <CenteredMessage title="Fetching config failed">
        <Typography variant="p" color="muted">
          Please reload the page.
        </Typography>
      </CenteredMessage>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.recaptcha_client_key}>
      {children(config)}
    </GoogleReCaptchaProvider>
  );
}
